/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { ActivatedRoute, Router } from '@angular/router';
import { BaseComponent } from '../../../../../core/components';
import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { ErrorResponseModel } from '../../../../../core/models';
import {
    LoadingService,
    LogService,
    ModalService,
    NavigationService,
    NotifyService,
    RbacService,
    SocketService,
    UserService,
    UtilsService
} from '../../../../../core/services';
import { LockApiService, PromptService, ServicesApiService, SystemApiService } from '../../../shared';
import { Observable, Subscriber } from 'rxjs';
import { ServiceModel } from '../../../shared/models/serviceModel';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-services',
    templateUrl: './services.component.html',
    styleUrls: ['./services.component.scss']
})

export class ServicesComponent extends BaseComponent implements OnInit {
    /**
     * Table row data
     */
    public tableRows: any[] = [];

    /**
     * Search term for searching the services table, also used with the search query parameter in the url
     */
    public searchTerm: string = '';

    /**
     * Notification ID needed to get and cancel the notification.
     */
    public notificationId: string;

    /**
     * Store active state of the API.
     */
    public apiActive: boolean = false;

    /**
     * Flag for checking if any services require a restart.
     */
    public servicesRequiringRestarts: boolean = false;

    /**
     * Store permissions in lookup table.
     */
    public permissionsLookup: any = {};

    /**
     * Store messages for error modal.
     */
    public modalErrorMessages: string[] = [];

    /**
     * Toggle shimmer effect on token container.
     */
    public effect: boolean = false;

    constructor(
        private activatedRoute: ActivatedRoute,
        private cdr: ChangeDetectorRef,
        private loadingService: LoadingService,
        private lockApiService: LockApiService,
        private logService: LogService,
        private modalService: ModalService,
        private navigationServices: NavigationService,
        private notifyService: NotifyService,
        private promptService: PromptService,
        private rbacService: RbacService,
        private router: Router,
        private servicesApiService: ServicesApiService,
        private socketService: SocketService,
        private systemService: SystemApiService,
        private userService: UserService,
        private utilsService: UtilsService
    ) {
        super();
    }

    public ngOnInit(): void {
        this.subscribeToQueryParams();
        this.setUpPageData();
        this.setUpServiceSubscriber();
        this.registerButtons();
    }

    /**
     * Send a service request - IE restart/reload.
     * @param service
     * @param request
     */
    public sendServiceRequest(service: string, request: string): void {
        const serviceRequest: string = request + 'Service';
        const state: 'Reloading' | 'Restarting' = request === 'reload' ? 'Reloading' : 'Restarting';
        const message: string = `${state} service. Please wait...`;
        this.actionService(service, serviceRequest, message);
    }

    /**
     * Send apply all pending services request via the service.
     */
    public applyAllChanges(): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            this.apiActive = true;
            this.notifyService.warning('Initiated apply all process. Services may take a few minutes to reload or restart');
            this.notifyService.muteLocalNotifications();
            this.servicesApiService.applyAll()
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (): void => {
                        observer.next();
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.logService.error('ServicesComponent: applyAllChanges', 'Error submitting data', error);
                        observer.error(error);
                    },
                    complete: (): void => {
                        this.notifyService.unmuteLocalNotifications();
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Update the search term class variable.
     * @param $event
     */
    public updateSearchTerm($event: any): void {
        if (!$event?.length) {
            this.searchTerm = '';
        }
        if (($event?.length) && !this.utilsService.compareArrays($event, this.searchTerm)) {
            this.searchTerm = $event;
            this.cdr.detectChanges();
        }
    }

    /**
     * Open errors modal.
     * @param messages
     */
    public openErrorModal(messages: string[]): void {
        this.modalErrorMessages = messages;
        this.modalService.openModalInParent('error-messages');
    }

    /**
     * Notify user the auth code has been copied to clipboard.
     */
    public copiedSuccess(): void {
        this.effect = true;
        const timerId = setTimeout(() => {
            this.effect = false;
            clearTimeout(timerId);
        }, 500);
    }

    /**
     * Close error modal.
     */
    public closeErrorLog(): void {
        this.modalService.close('error-messages');
    }

    /**
     * Confirmation modal for restart / shutdown appliance.
     * @param action
     */
    public confirmRestartShutdown(action: string): void {
        const confirmationObject = {
            title: action + '_page_title',
            message: 'Are you sure you would like to ' + action + ' this system?',
            callBack: this.restartShutdown.bind(this, action),
            options: { translateText: false }
        };
        this.modalService.confirmDecision(confirmationObject);
    }

    /**
     * Restart or shutdown the appliance.
     * @param action
     * @private
     */
    private restartShutdown(action: string) {
        return (action !== 'shutdown')
            ? this.restart()
            : this.shutdown();
    }

    /**
     * Restart appliance.
     * @private
     */
    private restart(): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            this.notifyService.muteLocalNotifications();
            this.systemService.restart()
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (): void => {
                        const user: any = this.userService.getUser();
                        this.userService.logout(false);
                        this.notifyService.unmuteLocalNotifications();
                        this.router.navigateByUrl('/restart', { state: { user } }).then();
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.shutdownRestartErrorHandler(error, 'restart');
                        this.logService.error(
                            'ServicesComponent: restart',
                            'Error submitting reboot request',
                            error
                        );
                    },
                    complete: (): void => {
                        observer.next();
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Shutdown appliance.
     * @private
     */
    private shutdown(): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            this.systemService.shutdown()
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (): void => {
                        const user: any = this.userService.getUser();
                        this.userService.logout(false);
                        this.router.navigateByUrl('/shutdown', { state: { user } }).then();
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.shutdownRestartErrorHandler(error, 'shutdown');
                        this.logService.error(
                            'ServicesComponent: shutdown',
                            'Error submitting shut down request', error);
                    },
                    complete: (): void => {
                        observer.next();
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Handle the error notification.
     * @param error
     * @param action
     * @private
     */
    private shutdownRestartErrorHandler(error: any, action: string): void {
        this.loadingService.loadingButtonEmitter.next(false);
        this.modalService.closeAll();
        this.notifyService.unmuteLocalNotifications();
        this.notificationId = `${action}-error`;
        if (action === 'shutdown') {
            this.notifyService.removeNotificationById('system-halt-put');
        }
        if (error.message.includes('locked by update')) {
            const callBack = { text: 'force_unlock', function: this.confirmUnlock.bind(this) };
            this.notifyService.error(error.message, { id: this.notificationId, callBack });
        } else {
            this.notifyService.error(error.message, { id: this.notificationId });
        }
    }

    /**
     * Confirm unlock modal.
     * @private
     */
    private confirmUnlock(): void {
        const confirmationObject = {
            title: 'system_control_unlock',
            message: 'system_control_confirm_unlock',
            callBack: this.unLockUserLock.bind(this),
            options: { translateText: true }
        };

        this.modalService.confirmDecision(confirmationObject);
    }

    /**
     * Unlock the user lock.
     * @private
     */
    private unLockUserLock(): Observable<any> {
        this.notifyService.removeNotificationById(this.notificationId);

        return new Observable(observer => {
            this.lockApiService.unLockUserLock()
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (): void => {
                        observer.next();
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Subscribe to inbound search query.
     * Note: Using a subscriber here because the notification can contain a link that can be actioned
     * by the user after the page has loaded.
     * @private
     */
    private subscribeToQueryParams(): void {
        this.activatedRoute.queryParams
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (params: any): void => {
                    if (params?.sFilter) {
                        this.searchTerm = this.utilsService.duplicateObject(params.sFilter);
                        this.navigationServices.removeParamsFromUrl();
                    }
                }
            });
    }

    /**
     * Subscribe to service status changes.
     * @private
     */
    private setUpPageData(): void {
        const localNodeServices: any = this.promptService.localNodeServices;
        this.populateTableRows(localNodeServices);
    }

    /**
     * Set up subscriptions to the prompt service.
     * @private
     */
    private setUpServiceSubscriber(): void {
        this.promptService.localServiceStatusEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (data: any): void => {
                    this.populateTableRows(data);
                    this.apiActive = false;
                    this.notifyService.removeNotificationById('notify_loading');
                    this.utilsService.debounce('tableUpdate', (): void => {
                        this.notifyService.success('Service statuses updated');
                    }, 1000);
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('ServicesComponent: setUpPAgeData() - failed to get data ', error.message);
                }
            });
    }

    /**
     * Populate the service list rows.
     * @param services
     * @private
     */
    private populateTableRows(services: ServiceModel[]): void {
        this.tableRows = [];
        services.forEach((service: ServiceModel): void => {
            this.tableRows.push({
                name: service.name,
                fullName: service.fullName,
                state: service.status === 'disabled' ? 'inactive' : service.status,
                stateClass: this.utilsService.getStatusDisplayName(service.status),
                canReload: service.canReload,
                action: service.action,
                messages: this.formatMessages(service.messages)
            });
        });

        this.tableRows = this.sortTableRowsByStatus(this.tableRows);
        this.servicesRequiringRestarts = this.promptService.hasServicesRequiringRestartReload();
    }

    /**
     * Format messages
     * @param messages
     * @private
     */
    private formatMessages(messages: string[]): string[] {
        if (messages.length) {
            const formattedMessages: string[] = [];
            for (const message of messages) {
                formattedMessages.push(`<span class="${this.getMessageClass(message)}">${message}</span>`);
            }

            return formattedMessages;
        }

        return messages;
    }

    /**
     * Get message class.
     * @param message
     * @private
     */
    private getMessageClass(message: string): string {
        if (message.includes('[EMERGENCY]') ||
            message.includes('[ALERT]') ||
            message.includes('[CRITICAL]') ||
            message.includes('[ERROR]')) {
            return 'status-text-error';
        }

        if (message.includes('[WARNING]')) {
            return 'status-text-warning';
        }

        if (message.includes('[NOTICE]') ||
            message.includes('[INFO]') ||
            message.includes('[DEBUG]')) {
            return 'status-text-info';
        }

        return 'status-text';
    }

    /**
     * Sort table rows by status.
     * @param data
     * @private
     */
    private sortTableRowsByStatus(data: any[]): any {
        // TODO: The empty string on the end of the below array should be removed when IPVSDirector is implemented.
        // TODO: At the moment there is no service so it returns nothing. This makes sure that empty services are placed
        // TODO: at the end of the list.
        const order: string[] = ['failed', 'reload', 'restart', 'active', 'inactive', 'disabled', ''];

        return this.utilsService.sortArrayByProperty(data, 'state', order);
    }

    /**
     * Make api call
     * @param service
     * @param action
     * @param message
     * @private
     */
    private actionService(service: string, action: string, message: string): void {
        this.apiActive = true;
        this.notifyService.info(message, { system: true, id: 'notify_loading' });
        this.servicesApiService[action](service)
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (): void => {
                    if (service === 'bear') {
                        this.socketService.connect();
                    }
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('ServiceComponent: actionService() ' + action, error.message);
                }
            })
            .add((): void => {
                this.loadingService.loadingActionDotsEmitter.next(false);
            });
    }

    /**
     * Register the page's buttons for RBAC.
     * @private
     */
    private registerButtons(): void {
        const buttonReference = {
            'restart-services-apply-services': [{ path: '/services/apply', methods: ['PUT'] }],
            'restart-services-restart-loadbalancer': [{ path: '/services/:service/reload', methods: ['PUT'] }],
            'system-control-shutdown-loadbalancer': [{ path: '/services/:service/restart', methods: ['PUT'] }],
            // All the buttons are the same.
            'restart-services-row-reload': [{ path: '/services/:service/reload', methods: ['PUT'] }],
            'restart-services-row-restart': [{ path: '/services/:service/restart', methods: ['PUT'] }]
        };

        this.permissionsLookup = this.rbacService.getPermissionsLookup(buttonReference);
    }
}
